import { HttpStatus } from 'constants/http-status';
import { ShopId } from 'constants/shop';
import { Product } from 'interfaces/product';
import { axios, axiosWithoutRequestInterceptor } from 'utils/axios';
import { ProductSegmentation, StaticPdp } from '../modules/product-details/types';
import { PersonalizedProductDetailsResponse } from './product/getPersonalizedProductDetails/types';

/**
 * Collection Product Response
 */
export interface CollectionProductResponse {
  metadata: {
    petsdeli: {
      uid: string;
      /** Sales number in last 7 days */
      salesCounter: number;
    };
    /** Metadata configured in Accentuate plugin on Shopify */
    accentuate: {
      ribbonText1: string;
      preorderText: string;
      /** @deprecated */
      hideCollections: Array<{
        index?: number;
        id: number;
      }>;
      subTitle: string;
      ribbonColor: string;
      /** Title for display purpose. */
      mainTitle: string;
    };
    /** Subscription config */
    subscription: {
      /** such as 2week,4week,6week,8week */
      intervals: string;
      enabled: boolean;
      variants: Array<{
        interval: '2week' | '4week' | '6week' | '8week';
        variantId: number;
        productId: number;
        originVariantId: number;
        price: number;
      }>;
    };
    rating: {
      count: string;
      score: string;
    };
  };
  handle: string;
  productType: string;
  productId: string;
  variants: Array<{
    availableForSale: boolean;
    image: {
      alt?: string;
      src: string;
    };
    weight: number;
    id: number;
    title: string;
    sku: string;
    priceV2: {
      currencyCode: string;
      amount: string;
    };
    weightUnit: 'KILOGRAMS' | 'GRAMS' | 'POUNDS' | 'OUNCES';
    compareAtPrice: number;
    points?: number;
  }>;
  _updatedAt: number;
  createdAt: string;
  /** Image of a product or a variant when a variant is assigned as product on collection dashboard */
  image: {
    alt?: string;
    src: string;
  };
  /** This property is assigned when a variant is assigned as product to promote a specific variant on dashboard */
  masterProductId?: number;
  availableForSale: boolean;
  /** Product Id. This property is assigned variantId when a variant is assigned as product to promote a specific variant on dashboard */
  id: number;
  tags: Array<string>;
  /** This title should NOT be used for display.
   * It is used as human readable "ID" for management purpose.
   * For display title, refer to metadata.accentuate.mainTitle
   * */
  title: string;
  /** Property for DB */
  _updated: number;
  /** Property for DB : Surrogate Key, reflection of id */
  sk: string;
  /** Property for DB : Prime Key */
  pk: string;
  /** Prooduct segmentation*/
  segmentation?: ProductSegmentation;
}

export interface CollectionResponse {
  handle: string;
  products: Array<CollectionProductResponse>;
  title: string;
}

export function isLegacyCollection(response: NonNullable<any>): response is CollectionResponse {
  return !!response && typeof response['handle'] !== 'undefined';
}

/**
 * @NOTE : CORS error occurs when requested from client side.
 * To fix the problem BE deployment is required.
 */
export const fetchCollectionProducts = async (
  collectionHandle: string,
  shopId: ShopId,
  /** Omit 404 error toast if true */
  shouldOmitError?: boolean
): Promise<CollectionResponse> => {
  const url =
    shopId === ShopId.CH
      ? `/api/legacy/collections/${collectionHandle}`
      : `/product/collection/${collectionHandle}`;

  const config = {
    isPdexApi: shopId === ShopId.CH,
    withoutAudience: false,
    ...(shouldOmitError
      ? {
          errorHandling: {
            statusesToOmit: [HttpStatus.NOT_FOUND],
          },
        }
      : {}),
  };
  return await axios.get<CollectionResponse>(url, config).then(({ data }) => data);
};

export type FetchProductDetailsResponse = StaticPdp | null;

/**
 * Fetch ProductDetails from productHandle and without audience header.
 * When according details doesn't exist, returns null;
 * @param productHandle
 * @param withoutAudience
 * @returns productDetails
 */
export const fetchProductDetails = async (
  productHandle: string,
  withoutAudience?: boolean,
  /** Omit 404 error toast if true */
  shouldOmitError?: boolean,
  axiosInstance?: typeof axios
): Promise<FetchProductDetailsResponse> => {
  const url = `/api/legacy/products/${productHandle}`;

  const config = {
    isPdexApi: true,
    withoutAudience,
    ...(shouldOmitError
      ? {
          errorHandling: {
            statusesToOmit: [HttpStatus.NOT_FOUND],
          },
        }
      : {}),
  };

  const client = axiosInstance || axios;

  try {
    return await client.get<StaticPdp>(url, { ...config }).then(({ data }) => data);
  } catch (error) {
    if (error.response && [403, 404].includes(error.response.status)) {
      return null;
    }
    throw error;
  }
};

export type NewProductSegmentation = Omit<
  PersonalizedProductDetailsResponse,
  'variants' | 'couponDefinition'
>;

export function isNewProductSegmentation(
  input: ProductSegmentation | NewProductSegmentation
): input is NewProductSegmentation {
  return input && typeof input['bestseller'] === 'boolean';
}

export const getPetType = (product: ProductType) => {
  const isForDogs = product.tags!.includes('Für Hunde');
  return isForDogs ? 'dog' : 'cat';
};
type ProductType = Pick<Product, 'id' | 'tags' | 'productType' | 'title'>;

export interface EstimatedTimeOfArrival {
  [key: string]: {
    variantId: string;
    eta: string;
    shopId: string;
  };
}

/**
 * Fetch Estimated Time Of Arrival (ETA) on Variant level
 */
export const fetchEstimatedTimeOfArrival = async (
  endpoint: string,
  variantsId: number[],
  shouldOmitError?: boolean
): Promise<EstimatedTimeOfArrival | null> => {
  try {
    const config = {
      ...(shouldOmitError
        ? {
            errorHandling: {
              statusesToOmit: [HttpStatus.NOT_FOUND, HttpStatus.TOO_MANY_REQUESTS],
            },
          }
        : {}),
    };
    const url = `${endpoint}/subscriptions-oos/get-variants-eta/`;
    const response = await axiosWithoutRequestInterceptor
      .post(url, { variantsId }, config)
      .then(({ data }) => data);
    return response;
  } catch (error) {
    if (error.response && [403, 404, 500, 400].includes(error.response.status)) {
      return null;
    }
    throw error;
  }
};
